import { useEffect, useRef, useState, useCallback } from "react";

import axios from "axios";
import { io, Socket } from "socket.io-client";

import { ApplicationEndpoints } from "@generated/application";
import { apiBaseUrls } from "@platform/constants/shared-constants";

import { useStackRealtime } from "./useStackRealtime";

// ── Types (mirror backend pod-status.types.ts) ────────────────────────────────

export interface PodStatusSnapshot {
  podName: string;
  serviceName: string;
  /** System alias — from rusha.io/system label. Undefined for shared project-level services. */
  systemAlias?: string;
  phase: string;
  ready: boolean;
  restarts: number;
  startedAt: string | null;
  cpu?: string;
  memory?: string;
}

export interface PodMonitorSnapshot {
  namespace: string;
  /** Project alias derived from the namespace (e.g., "customer-portal" from "acme-corp-customer-portal"). */
  projectAlias?: string;
  timestamp: string;
  pods: PodStatusSnapshot[];
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export interface UsePodMonitorResult {
  snapshot: PodMonitorSnapshot | null;
  loading: boolean;
  error: string | null;
  connected: boolean;
  refresh: () => void;
}

/**
 * Real-time pod monitoring hook.
 *
 * 1. On mount: fetches a one-shot snapshot from `GET /api/v1/applications/:alias/pods`
 * 2. Opens a Socket.io connection to `/pods` namespace (websocket transport only)
 * 3. Subscribes to the alias — backend streams PodMonitorSnapshot on each K8s event
 * 4. Socket.io auto-reconnects on disconnect — no polling fallback needed
 * 5. On unmount: unsubscribes and disconnects the socket
 *
 * When inside a StackRealtimeProvider, returns shared context data
 * instead of opening its own WebSocket connection.
 */
export function usePodMonitor(
  alias: string | undefined,
  appEnv: string,
): UsePodMonitorResult {
  // ── Context consumption ──────────────────────────────────────────────────
  const ctx = useStackRealtime();

  // ── Fallback: always declare hooks to satisfy rules-of-hooks ──────────
  const [snapshot, setSnapshot] = useState<PodMonitorSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const fetchSnapshot = useCallback(async () => {
    if (!alias) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION_PODS.replace(":id", alias)}?environment=${encodeURIComponent(appEnv)}`;
      const res = await axios.get<PodMonitorSnapshot>(url, {
        withCredentials: true,
      });
      setSnapshot(res.data);
    } catch {
      setError("Failed to load pod status.");
    } finally {
      setLoading(false);
    }
  }, [alias, appEnv]);

  useEffect(() => {
    if (!alias) return;

    // If inside a StackRealtimeProvider, don't open a WebSocket —
    // the SSE context provides all real-time data.
    if (ctx) return;

    // Initial REST snapshot
    void fetchSnapshot();

    // Open WebSocket — connect to the /pods namespace
    const socket = io(`${apiBaseUrls.BACKEND}/pods`, {
      withCredentials: true,
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 20,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("subscribe", { alias, appEnv });
    });

    socket.on("pods", (data: PodMonitorSnapshot) => {
      setSnapshot(data);
      setError(null);
    });

    socket.on("error", (data: { message: string }) => {
      setError(data.message);
    });

    socket.on("disconnect", () => {
      setConnected(false);
    });

    return () => {
      socket.emit("unsubscribe", { alias });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [alias, appEnv, fetchSnapshot, ctx]);

  // ── Context short-circuit: return shared data if available ──────────────
  if (ctx) {
    return {
      snapshot: ctx.podSnapshot,
      loading: ctx.podLoading,
      error: ctx.podError,
      connected: ctx.podsConnected,
      refresh: ctx.refetchPods,
    };
  }

  return {
    snapshot,
    loading,
    error,
    connected,
    refresh: fetchSnapshot,
  };
}
